import * as React from "react"

import { cva } from "./cva.ts"

export function LinesShader({
  className,
  showGradient = true,
}: React.ComponentProps<"div"> & {
  showGradient?: boolean
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (!window.WebGLRenderingContext) {
      console.error("WebGL not supported in this environment")
      return
    }

    let animationFrameId: number

    if (!canvasRef.current || !containerRef.current) {
      console.error("Canvas or container ref not available")
      return
    }

    const gl = canvasRef.current.getContext("webgl")!
    const vertexShader = gl.createShader(gl.VERTEX_SHADER)

    if (!vertexShader) return

    gl.shaderSource(vertexShader, vertexShaderSource)
    gl.compileShader(vertexShader)

    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      console.error("Vertex Shader Compile Error:", gl.getShaderInfoLog(vertexShader))

      return
    }

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)

    if (!fragmentShader) return

    gl.shaderSource(fragmentShader, fragmentShaderSource)
    gl.compileShader(fragmentShader)

    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      console.error("Fragment Shader Compile Error:", gl.getShaderInfoLog(fragmentShader))
      return
    }

    const shaderProgram = gl.createProgram()

    if (!shaderProgram) return

    gl.attachShader(shaderProgram, vertexShader)
    gl.attachShader(shaderProgram, fragmentShader)
    gl.linkProgram(shaderProgram)

    if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
      console.error("Program Link Error:", gl.getProgramInfoLog(shaderProgram))

      return
    }

    // Geometry
    const positionBuffer = gl.createBuffer()

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0]),
      gl.STATIC_DRAW,
    )

    const updateCanvasSize = () => {
      if (canvasRef.current && containerRef.current && gl) {
        const { width, height } = containerRef.current.getBoundingClientRect()
        canvasRef.current.width = width
        canvasRef.current.height = height
        gl.viewport(0, 0, width, height)
      }
    }

    const render = (time: number) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)

      const position = gl.getAttribLocation(shaderProgram, "position")

      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
      gl.enableVertexAttribArray(position)

      gl.useProgram(shaderProgram)

      const u_time = gl.getUniformLocation(shaderProgram, "u_time")

      gl.uniform1f(u_time, time * 0.001)

      const u_resolution = gl.getUniformLocation(shaderProgram, "u_resolution")

      const { width, height } = containerRef.current?.getBoundingClientRect() || {
        width: window.innerWidth,
        height: window.innerHeight,
      }
      gl.uniform2f(u_resolution, width, height)

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      animationFrameId = requestAnimationFrame(render)
    }

    const resizeObserver = new ResizeObserver(updateCanvasSize)
    resizeObserver.observe(containerRef.current)

    // Initial size setup
    updateCanvasSize()

    animationFrameId = requestAnimationFrame(render)

    return () => {
      cancelAnimationFrame(animationFrameId)
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div ref={containerRef} className={classNameForContainer({ className })}>
      <canvas ref={canvasRef} className="absolute inset-0 opacity-30" />
      {showGradient && (
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black to-black via-transparent" />
      )}
    </div>
  )
}

const classNameForContainer = cva("invert dark:invert-0 overflow-hidden")

// #region Shaders

const vertexShaderSource = `
  attribute vec2 position;
  void main() {
      gl_Position = vec4(position, 0.0, 1.0);
  }
`

const fragmentShaderSource = `
  precision highp float;

  uniform vec2 u_resolution;
  uniform float u_time;

  // Functions Author: TechDev 
  float Bayer2(vec2 a, float t) {
    a = floor(a);
    float anim = t * 0.15;
      return fract(length(vec2(a.x, a.y * 1.25))/12.0 + anim);
  }
  #define Bayer4(a, t) (Bayer2(0.5 * (a), t) / 4.0 + Bayer2(a, t))
  #define Bayer8(a, t) (Bayer4(0.5 * (a), t) / 4.0 + Bayer2(a, t))
  #define Bayer16(a, t) (Bayer8(0.5 * (a), t) / 4.0 + Bayer2(a, t))
  #define Bayer32(a, t) (Bayer16(0.5 * (a), t) / 4.0 + Bayer2(a, t))
  #define Bayer64(a, t) (Bayer32(0.5 * (a), t) / 60.0 + Bayer2(a, t))
    
  void main() {
    vec2 uv = (gl_FragCoord.xy - u_resolution * 0.5) / u_resolution.y;    
      vec2 pixel = 1.0/u_resolution;
      vec2 uv2 = gl_FragCoord.xy * pixel;
    float t = u_time * 0.2;
    // Size
      uv2 *= 1.5;
      // Mirror
      uv2.x *= -1.0;
      // Angle
      uv2.x /= uv2.y * 1.25 - 2.0;
    // Flow
    uv2.x -= sin(t * 0.075);
      uv2.x += 5.0;
      uv2.y -= 1.7;
      float dithering = (
          Bayer64(
              uv2 * 300.0,
              t * 1.5
          ) * 30.0
      );
      uv.y -= dithering;
      vec3 color = vec3(uv.y > -2.0);
      vec3 c1 = vec3(0.0, 0.0, 0.0);
      vec3 c2 = vec3(0.4);
      color = mix(c1, c2, color);
    gl_FragColor = vec4(color, 1.0);
  }
`

// #endregion
